import { useNavigate } from '@tanstack/react-router';
import {
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    Circle,
    CircleCheck,
    type LucideIcon,
    MessageSquare,
    PanelRightClose,
    PhoneCall,
    PhoneForwarded,
    PhoneMissed,
    RotateCcw,
    UserPlus,
    Voicemail,
} from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import { Spinner } from '@apps/web/components/ui/spinner';
import { bucketContactActivity } from '@apps/web/lib/contact_activity_buckets';
import { formatRelativeTime } from '@apps/web/lib/date_utils';
import { EVENTS } from '@apps/web/lib/events';
import { cn } from '@apps/web/lib/utils';
import {
    type ContactActivityItem,
    type ContactActivityItemType,
    useContactActivity,
} from '@apps/web/swr_data_fetching/contact_activity';
import { assert } from '@libraries/typescript/utilities';

export function iconForType(type: ContactActivityItemType): LucideIcon {
    switch (type) {
        case 'call_completed':
            return PhoneCall;
        case 'call_missed':
            return PhoneMissed;
        case 'call_voicemail':
            return Voicemail;
        case 'call_transferred':
            return PhoneForwarded;
        case 'activity_open':
            return Circle;
        case 'activity_resolved':
            return CheckCircle2;
        case 'conversation_assigned':
            return UserPlus;
        case 'conversation_closed':
            return CircleCheck;
        case 'conversation_reopened':
            return RotateCcw;
        case 'staff_message':
            return MessageSquare;
        default:
            return assert.never(type);
    }
}

// Semantic per-type icon colors, drawn from the same palette the activity
// feed uses (emerald for resolved/completed, red for missed, amber for open,
// blue/purple for calls and assignments) so the two surfaces read alike.
export function iconColorForType(type: ContactActivityItemType): string {
    switch (type) {
        case 'call_completed':
            return 'text-emerald-600';
        case 'call_missed':
            return 'text-red-600';
        case 'call_voicemail':
            return 'text-purple-600';
        case 'call_transferred':
            return 'text-blue-600';
        case 'activity_open':
            return 'text-amber-600';
        case 'activity_resolved':
            return 'text-emerald-600';
        case 'conversation_assigned':
            return 'text-blue-600';
        case 'conversation_closed':
            return 'text-emerald-600';
        case 'conversation_reopened':
            return 'text-amber-600';
        case 'staff_message':
            return 'text-sky-600';
        default:
            return assert.never(type);
    }
}

// The panel is scoped to a single contact, so a leading "<contact name>:"
// prefix on an item title is redundant noise — strip it so the meaningful
// part of the title gets the width.
export function stripContactNamePrefix(
    title: string,
    contactName: string | null,
): string {
    if (!contactName) {
        return title;
    }
    const prefix = `${contactName}:`;
    if (title.startsWith(prefix)) {
        return title.slice(prefix.length).trimStart();
    }
    return title;
}

type ContactActivityNavigation =
    | {
          readonly to: '/voice-calls';
          readonly search: { readonly voiceCallId: string };
      }
    | {
          readonly to: '/activity';
          readonly search: { readonly eventId: string };
      }
    | {
          readonly to: '/conversations';
          readonly search: { readonly conversationId: string };
      };

/**
 * Maps a timeline item to its deep-link target. Returns null when there is
 * nothing to open (e.g. a conversation/message row that points at the already
 * open conversation, or a row missing the id needed to navigate).
 */
export function resolveContactActivityNavigation(
    item: ContactActivityItem,
    openConversationId: string,
): ContactActivityNavigation | null {
    switch (item.type) {
        case 'call_completed':
        case 'call_missed':
        case 'call_voicemail':
        case 'call_transferred':
            return item.voice_call_id
                ? {
                      to: '/voice-calls',
                      search: { voiceCallId: item.voice_call_id },
                  }
                : null;
        case 'activity_open':
        case 'activity_resolved':
            return item.activity_event_id
                ? {
                      to: '/activity',
                      search: { eventId: item.activity_event_id },
                  }
                : null;
        case 'conversation_assigned':
        case 'conversation_closed':
        case 'conversation_reopened':
        case 'staff_message':
            return item.conversation_id &&
                item.conversation_id !== openConversationId
                ? {
                      to: '/conversations',
                      search: { conversationId: item.conversation_id },
                  }
                : null;
        default:
            return assert.never(item.type);
    }
}

export function ContactActivityRow({
    item,
    contactName,
    onSelect,
}: {
    readonly item: ContactActivityItem;
    readonly contactName: string | null;
    readonly onSelect: (item: ContactActivityItem) => void;
}) {
    const Icon = iconForType(item.type);
    const title = stripContactNamePrefix(item.title, contactName);
    return (
        <button
            type="button"
            className="hover:bg-muted/60 flex w-full items-start gap-2.5 rounded-md py-1.5 pr-4 pl-2 text-left transition-colors"
            onClick={() => onSelect(item)}
        >
            <Icon
                className={cn(
                    'mt-0.5 h-4 w-4 shrink-0',
                    iconColorForType(item.type),
                )}
            />
            <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                    <p className="text-foreground min-w-0 flex-1 text-sm font-medium break-words">
                        {title}
                    </p>
                    <span className="text-muted-foreground shrink-0 text-xs">
                        {formatRelativeTime(item.occurred_at)}
                    </span>
                </div>
                {item.detail ? (
                    <p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs">
                        {item.detail}
                    </p>
                ) : null}
                {item.actor_user_name ? (
                    <p className="text-muted-foreground/80 mt-0.5 text-xs">
                        {item.type === 'activity_resolved'
                            ? `Resolved by ${item.actor_user_name}`
                            : item.actor_user_name}
                    </p>
                ) : null}
            </div>
        </button>
    );
}

export function ContactActivityPanel({
    organizationId,
    conversationId,
    contactName = null,
    timeZone,
    onClose,
}: {
    readonly organizationId: string;
    readonly conversationId: string;
    readonly contactName?: string | null;
    readonly timeZone: string;
    readonly onClose: () => void;
}) {
    const navigate = useNavigate();
    const posthog = usePostHog();

    // Manual cursor pagination: `cursor` is the page currently fetched; items
    // accumulate across pages in `items`. Each page is merged exactly once,
    // keyed by its request cursor.
    const [cursor, setCursor] = useState<string | null>(null);
    const [items, setItems] = useState<ReadonlyArray<ContactActivityItem>>([]);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const mergedCursorsRef = useRef<Set<string>>(new Set());

    const { data, error, isLoading, isValidating } = useContactActivity(
        organizationId,
        conversationId,
        cursor,
        true,
    );

    // Reset accumulation when switching conversations.
    useEffect(() => {
        setCursor(null);
        setItems([]);
        setNextCursor(null);
        mergedCursorsRef.current = new Set();
    }, [conversationId]);

    useEffect(() => {
        if (!data) {
            return;
        }
        const cursorKey = cursor ?? '__first__';
        if (mergedCursorsRef.current.has(cursorKey)) {
            return;
        }
        mergedCursorsRef.current.add(cursorKey);
        setItems((previous) => [...previous, ...data.items]);
        setNextCursor(data.next_cursor);
    }, [data, cursor]);

    const buckets = useMemo(
        () => bucketContactActivity(items, timeZone, new Date()),
        [items, timeZone],
    );

    const handleSelect = (item: ContactActivityItem) => {
        posthog.capture(EVENTS.CONTACT_ACTIVITY_ROW_CLICKED, {
            type: item.type,
        });
        const target = resolveContactActivityNavigation(item, conversationId);
        if (target) {
            void navigate(target);
        }
    };

    const handleLoadMore = () => {
        if (!nextCursor) {
            return;
        }
        posthog.capture(EVENTS.CONTACT_ACTIVITY_LOAD_MORE);
        setCursor(nextCursor);
    };

    const isInitialLoading = isLoading && items.length === 0;
    const isLoadingMore = isValidating && items.length > 0;

    return (
        <div className="flex h-full min-h-0 flex-col">
            {/* h-12 matches the chat panel header, so the two border lines
                meet at the same height across the panel divider. */}
            <div className="flex h-12 shrink-0 items-center justify-between border-b px-4">
                <h2 className="text-sm font-semibold">Activity Timeline</h2>
                <Button
                    aria-label="Close activity timeline"
                    className="h-8 w-8"
                    onClick={onClose}
                    size="icon"
                    type="button"
                    variant="ghost"
                >
                    <PanelRightClose className="h-4 w-4" />
                </Button>
            </div>
            <ScrollArea className="min-h-0 flex-1">
                <div className="p-2">
                    {isInitialLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Spinner className="h-5 w-5" />
                        </div>
                    ) : error ? (
                        <p className="text-muted-foreground px-2 py-8 text-center text-sm">
                            Couldn’t load activity.
                        </p>
                    ) : buckets.length === 0 ? (
                        <p className="text-muted-foreground px-2 py-8 text-center text-sm">
                            No activity yet
                        </p>
                    ) : (
                        <Fragment>
                            {buckets.map((bucket) => (
                                <Collapsible
                                    key={bucket.key}
                                    defaultOpen={true}
                                    className="mb-1"
                                >
                                    <CollapsibleTrigger className="group text-muted-foreground hover:text-foreground flex w-full items-center gap-1.5 px-2 py-1.5 text-xs font-semibold tracking-wide">
                                        <ChevronRight className="h-3 w-3 shrink-0 group-data-[state=open]:hidden" />
                                        <ChevronDown className="hidden h-3 w-3 shrink-0 group-data-[state=open]:block" />
                                        <span>
                                            {bucket.label} ({bucket.count})
                                        </span>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        {bucket.items.map((item) => (
                                            <ContactActivityRow
                                                key={item.id}
                                                item={item}
                                                contactName={contactName}
                                                onSelect={handleSelect}
                                            />
                                        ))}
                                    </CollapsibleContent>
                                </Collapsible>
                            ))}
                            {nextCursor ? (
                                <div className="px-2 py-2">
                                    <Button
                                        className="w-full"
                                        disabled={isLoadingMore}
                                        onClick={handleLoadMore}
                                        size="sm"
                                        type="button"
                                        variant="outline"
                                    >
                                        {isLoadingMore ? (
                                            <Spinner className="h-4 w-4" />
                                        ) : (
                                            'Load more'
                                        )}
                                    </Button>
                                </div>
                            ) : null}
                        </Fragment>
                    )}
                </div>
            </ScrollArea>
        </div>
    );
}
